import { LuCalendar, LuHash, LuLayers, LuPencil } from "react-icons/lu"
import { Link } from "react-router-dom"

import Badge from "~/v2/components/ui/badge"
import Button from "~/v2/components/ui/button"
import useThumbnail from "~/v2/hooks/use-thumbnail"
import type { VaasListItem } from "~/v2/types/vaas"

const statusVariantMap = {
  archived: "warning",
  draft: "muted",
  published: "success",
  unpublished: "muted",
} as const satisfies Record<
  VaasListItem["status"],
  "muted" | "success" | "warning"
>

const VaasCard = ({ item }: { item: VaasListItem }) => {
  const thumbnailUrl = useThumbnail(item.course?.thumbnail, 640)

  return (
    <article className="flex h-full overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-sm">
      <div className="flex w-full flex-col">
        <Link
          aria-label={`Open ${item.name}`}
          className="relative block aspect-[16/9] overflow-hidden bg-muted"
          to={`/vaas/${item.courseId}`}
        >
          {thumbnailUrl ? (
            <img
              alt=""
              className="h-full w-full object-cover"
              src={thumbnailUrl}
            />
          ) : (
            <div className="h-full w-full bg-[radial-gradient(circle_at_25%_20%,var(--primary),transparent_26%),linear-gradient(135deg,var(--muted),var(--primary)_55%,var(--accent))]" />
          )}
          <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 bg-gradient-to-t from-black/75 via-black/10 to-transparent p-3">
            <Badge
              className="rounded-md px-2 py-0.5 text-[11px] capitalize leading-5 shadow-sm"
              variant={statusVariantMap[item.status]}
            >
              {item.status}
            </Badge>
            <Badge className="rounded-md bg-background px-2 py-0.5 text-[11px] leading-5 text-foreground shadow-sm">
              v{item.version || "0.0.1"}
            </Badge>
          </div>
        </Link>

        <div className="flex flex-1 flex-col gap-3 p-4">
          <Link className="block no-underline" to={`/vaas/${item.courseId}`}>
            <h2 className="line-clamp-1 text-base font-semibold tracking-[-0.01em] text-foreground">
              {item.name}
            </h2>
          </Link>

          <div className="grid gap-2 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <LuHash className="h-4 w-4 text-muted-foreground" />
              <span>ID {item.vaasId || "No VAAS UUID"}</span>
            </div>
            <div className="flex items-center gap-2">
              <LuLayers className="h-4 w-4 text-muted-foreground" />
              <span>
                {item.lessonCount} lesson{item.lessonCount === 1 ? "" : "s"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <LuCalendar className="h-4 w-4 text-muted-foreground" />
              <span>Updated {item.updatedLabel}</span>
            </div>
          </div>

          <div className="mt-auto flex items-center gap-3">
            <Link className="flex-1" to={`/vaas/${item.courseId}`}>
              <Button
                className="h-9 w-full rounded-md text-sm font-semibold"
                variant="outline"
              >
                <LuPencil className="mr-2 h-4 w-4" />
                Edit
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </article>
  )
}

export default VaasCard
